import dayjs from 'dayjs';
import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {Box, Tooltip} from '@mui/material';
import {Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import {CampaignProps} from '@/types/campaign';
import {SocialStreamTab} from '@/types/socialMediaPost';
import CampaignStatsSummary from '@/views/campaigns/stats/components/campaign-stats-summary/CampaignStatsSummary';
import getCampaignStatsPresets from '@/views/campaigns/stats/components/campaignStatsDatePresets';
import CreatorsStatsTable from '@/views/campaigns/stats/components/creators-stats-table';
import OrganicPostsTable from '@/views/campaigns/stats/components/organic-posts-table';
import useCampaignStatsCreatorsController from '@/views/campaigns/stats/creators-content/useCampaignStatsCreatorsController';
import useOrganicContentController from '@/views/campaigns/stats/organic-content/useOrganicContentController';
import DateRangePicker from '@/components/date-range-picker/DateRangePicker';
import Sidebar, {SidebarItem} from '@/components/sidebar/Sidebar';
import styles from './CampaignStatisticsContent.module.scss';
import {StatisticsSection} from './statisticsSection';

// USE-405: TikTok is temporarily excluded from the date filter while data
// issues are resolved. To re-enable, add 'tiktok_v2' here, remove the
// `isTikTok` guard on `dateFilterChip` below, and restore "and TikTok Feed"
// in the unsupported-network tooltip copy.
const DATE_FILTER_NETWORKS = ['instagram'];

interface CampaignStatisticsContentProps {
  campaignId: string;
  campaign?: CampaignProps;
  network?: string | null;
  supportsStories?: boolean;
  supportsReels?: boolean;
  socialStream: SocialStreamTab;
  onTabChange: (stream: SocialStreamTab) => void;
  statisticsSection: StatisticsSection;
  onSectionChange: (section: StatisticsSection) => void;
}

function CampaignStatisticsContent({
  campaignId,
  campaign,
  network,
  supportsStories = false,
  supportsReels = false,
  socialStream,
  onTabChange,
  statisticsSection,
  onSectionChange,
}: CampaignStatisticsContentProps) {
  const isPostsSection = statisticsSection === 'posts';
  const hasClickTracking = (campaign?.campaignTrackingUrls?.length ?? 0) > 0;

  const {
    posts,
    campaignStats,
    searchQuery,
    sort,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    error: postsError,
    streamCounts,
    dateRange,
    isDateFiltered,
    refetch: refetchPosts,
    handleTabChange,
    handleSearch,
    handleSort,
    handleLoadMore,
    handleDownloadCsv,
    handleDateRangeChange,
    handleClearDateRange,
  } = useOrganicContentController({
    campaignId,
    supportsStories,
    supportsReels,
    onTabChange,
    socialStreamOverride: socialStream,
    includeStreamCounts: true,
    enabled: isPostsSection,
  });

  const {
    creators,
    searchQuery: creatorsSearchQuery,
    sort: creatorsSort,
    isLoading: isLoadingCreators,
    isFetching: isFetchingCreators,
    isFetchingNextPage: isFetchingNextCreatorsPage,
    hasNextPage: hasNextCreatorsPage,
    error: creatorsError,
    totalCount: creatorsTotalCount,
    refetch: refetchCreators,
    handleSearch: handleCreatorsSearch,
    handleSort: handleCreatorsSort,
    handleLoadMore: handleCreatorsLoadMore,
  } = useCampaignStatsCreatorsController({
    campaignId,
    hasClickTracking,
    enabled: !isPostsSection,
  });

  const {campaignStatsDateFilter, campaignStatsEmv} = useFlags();
  const supportsDateFilter =
    DATE_FILTER_NETWORKS.includes(network ?? '') &&
    (socialStream === 'feed' || socialStream === 'reels');
  const isTikTok = network === 'tiktok_v2';

  const presets = getCampaignStatsPresets();

  const dateRangeLabel = isDateFiltered
    ? `${dayjs(dateRange.startDate).format(MONTH_SHORT_DAY_YEAR_FORMAT)} – ${dayjs(dateRange.endDate).format(MONTH_SHORT_DAY_YEAR_FORMAT)}`
    : undefined;

  const counts = streamCounts ?? {all: 0, feed: 0, story: 0, reels: 0};
  const sidebarItems: SidebarItem[] = [
    {id: 'all', label: translate('All'), count: counts.all},
    {id: 'feed', label: translate('Post'), count: counts.feed},
  ];

  if (supportsStories) {
    sidebarItems.push({
      id: 'story',
      label: translate('Stories'),
      count: counts.story,
    });
  }

  if (supportsReels) {
    sidebarItems.push({
      id: 'reels',
      label: translate('Reel'),
      count: counts.reels,
    });
  }

  sidebarItems.push({
    id: 'creators',
    label: translate('Creators'),
    count: creatorsTotalCount,
  });

  const selectedSidebarId = isPostsSection ? socialStream : 'creators';

  const handleSidebarSelect = (itemId: string) => {
    if (itemId === 'creators') {
      onSectionChange('creators');
      return;
    }

    onSectionChange('posts');
    handleTabChange(itemId as SocialStreamTab);
  };

  const error = isPostsSection ? postsError : creatorsError;

  if (error) {
    return (
      <Box className={styles.container}>
        <Box className={styles.errorContainer}>
          <ErrorOutlineIcon className={styles.errorIcon} />
          <Box className={styles.errorTitle}>
            {translate(
              isPostsSection
                ? 'Unable to Load Posts'
                : 'Unable to Load Creators'
            )}
          </Box>
          <Box className={styles.errorMessage}>
            {translate(
              isPostsSection
                ? 'Something went wrong while loading the posts. Please try again.'
                : 'Something went wrong while loading creator stats. Please try again.'
            )}
          </Box>
          <Button
            size="medium"
            appearance="neutral"
            mode="filled"
            onClick={() =>
              isPostsSection ? refetchPosts() : refetchCreators()
            }
          >
            {translate('Retry')}
          </Button>
        </Box>
      </Box>
    );
  }

  const dateFilterChip =
    campaignStatsDateFilter && !isTikTok && isPostsSection ? (
      <Tooltip
        title={
          supportsDateFilter
            ? ''
            : translate(
                'Snapshot data is currently only available for Instagram Feed/Reels'
              )
        }
        arrow
      >
        <span className={styles.dateFilterContainer}>
          {supportsDateFilter ? (
            <DateRangePicker
              testID="campaign-stats-date-range-picker"
              initialDateRange={dateRange}
              definedPresets={presets}
              onChange={handleDateRangeChange}
              showPresets
              maxDate={new Date()}
              renderInput={() => (
                <Button
                  size="small"
                  appearance="neutral"
                  mode="plain"
                  onClick={() => {}}
                >
                  <Icon
                    name="Navigation-Calendar"
                    size="small"
                    appearance="neutral"
                  />
                  {dateRangeLabel ?? translate('Filter by date')}
                </Button>
              )}
            />
          ) : (
            <Button size="small" appearance="neutral" mode="plain" disabled>
              <Icon
                name="Navigation-Calendar"
                size="small"
                appearance="neutral"
              />
              {translate('Filter by date')}
            </Button>
          )}
          {isDateFiltered && (
            <Button
              size="small"
              appearance="neutral"
              mode="plain"
              aria-label={translate('Clear date filter')}
              onClick={handleClearDateRange}
            >
              <Icon
                name="Actions-Close-Normal"
                size="small"
                appearance="neutral"
              />
            </Button>
          )}
        </span>
      </Tooltip>
    ) : null;

  return (
    <Box className={styles.container}>
      <Box className={styles.layout}>
        <Box className={styles.sidebarSticky}>
          <Sidebar
            testID="statistics-sidebar"
            title={translate('Statistics')}
            items={sidebarItems}
            selectedId={selectedSidebarId}
            onSelect={handleSidebarSelect}
          />
        </Box>

        <Box className={styles.mainContent}>
          {isPostsSection ? (
            <>
              <Box className={styles.statsSection}>
                <CampaignStatsSummary
                  testID="organic-content-stats-summary"
                  stats={campaignStats}
                  socialStream={socialStream}
                  network={network}
                  isLoading={isLoading}
                  defaultExpanded
                  dateRangeLabel={dateRangeLabel}
                  showEmv={!!campaignStatsEmv && !isDateFiltered}
                />
              </Box>

              <Box className={styles.tableSection}>
                <Box className={styles.tableWrapper}>
                  <OrganicPostsTable
                    testID="organic-content-table"
                    posts={posts}
                    network={network}
                    socialStream={socialStream}
                    totalCount={streamCounts?.[socialStream] ?? posts.length}
                    onShareAll={handleDownloadCsv}
                    isLoading={isLoading}
                    isFetching={isFetching || isFetchingNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    onLoadMore={handleLoadMore}
                    onSort={handleSort}
                    onSearch={handleSearch}
                    searchQuery={searchQuery}
                    sort={sort}
                    toolbarExtra={dateFilterChip}
                    isDateFiltered={isDateFiltered}
                    showEmv={!!campaignStatsEmv && !isDateFiltered}
                    emvTooltip={campaignStats?.emvFormula?.tooltip}
                  />
                </Box>
              </Box>
            </>
          ) : (
            <Box className={styles.tableSection}>
              <Box className={styles.tableWrapper}>
                <CreatorsStatsTable
                  testID="creators-stats-table"
                  creators={creators}
                  campaign={campaign}
                  totalCount={creatorsTotalCount}
                  isLoading={isLoadingCreators}
                  isFetching={isFetchingCreators || isFetchingNextCreatorsPage}
                  isFetchingNextPage={isFetchingNextCreatorsPage}
                  hasNextPage={hasNextCreatorsPage}
                  onLoadMore={handleCreatorsLoadMore}
                  onSort={handleCreatorsSort}
                  onSearch={handleCreatorsSearch}
                  searchQuery={creatorsSearchQuery}
                  sort={creatorsSort}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default CampaignStatisticsContent;
